import React from "react";
import { Link } from "react-router-dom";
import {
    Instagram, Music2, Youtube, Mail,
    Sparkles, ArrowRight, Quote, Heart,
    BookOpen, Flame, Users, Globe, Play, Film,
} from "lucide-react";

const HERO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/ca15f75e-ad30-44ae-956b-53f64afe519e/images/7a9a97580df3aeba40322e44c42f996ef4c890f6dc8dd63212c6b2efd3b9cd5a.png";
const KITCHEN_IMG =
    "https://static.prod-images.emergentagent.com/jobs/ca15f75e-ad30-44ae-956b-53f64afe519e/images/18c4802e009a11c6d1d82d43ade5886b41237748935a547662c259504a22b3de.png";

const GALLERY = [
    {
        src: "https://images.unsplash.com/photo-1530841377377-3ff06c0ca713?auto=format&fit=crop&w=1400&q=80",
        alt: "Greek coastal village at golden hour",
        caption: "The villages",
    },
    {
        src: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?auto=format&fit=crop&w=1400&q=80",
        alt: "Cold-pressed olive oil",
        caption: "The olive oil",
    },
    {
        src: "https://images.unsplash.com/photo-1466637574441-749b8f19452f?auto=format&fit=crop&w=1400&q=80",
        alt: "Wild herbs on a wooden board",
        caption: "The herbs",
    },
    {
        src: "https://images.unsplash.com/photo-1564759224907-65b945ff0e84?auto=format&fit=crop&w=1400&q=80",
        alt: "Mediterranean coastline",
        caption: "The sea",
    },
    {
        src: "https://images.pexels.com/photos/539451/pexels-photo-539451.jpeg?auto=compress&cs=tinysrgb&w=1400",
        alt: "A bowl of village soup",
        caption: "The table",
    },
    {
        src: "https://images.unsplash.com/photo-1603360946369-dc9bb6258143?auto=format&fit=crop&w=1400&q=80",
        alt: "Souvlaki on the grill",
        caption: "The fire",
    },
];

const TIMELINE = [
    {
        no: "01",
        year: "How we met",
        title: "Two Greeks, one kitchen",
        body:
            "One Cypriot. One Greek. Haris and Michael — two Greeks living abroad who met and connected instantly through their love for the culture, the traditions, and the food they had grown up with. Within weeks, that connection had a name.",
    },
    {
        no: "02",
        year: "The first videos",
        title: "Cooking, then sharing",
        body:
            "We started with simple recipes filmed in our own kitchen — fasolada, halloumi, souvlaki — the dishes our families made. The internet leaned in faster than we expected. Comments arrived from Greeks abroad telling us our food smelled like their grandmother's kitchen.",
    },
    {
        no: "03",
        year: "Something bigger",
        title: "The stories took over",
        body:
            "We realised quickly that people weren't only connecting with the recipes — they were connecting with the history behind them. Dishes from villages, from times of war and poverty, from celebration, migration and survival. Every recipe carried a story worth preserving.",
    },
    {
        no: "04",
        year: "Today",
        title: "A cultural archive",
        body:
            "What started as cooking videos has grown into a digital cookbook and cultural archive — read across more than forty countries by people who love Mediterranean food, real stories, and the quiet act of keeping traditions alive for the generation that comes next.",
    },
];

const STATS = [
    { icon: Users, value: "40K+", label: "Followers across platforms" },
    { icon: Play, value: "3M+", label: "Total views" },
    { icon: Film, value: "90+", label: "Video recipes" },
    { icon: Globe, value: "40+", label: "Countries reached" },
];

const TESTIMONIALS = [
    {
        name: "Eleni",
        location: "Melbourne, Australia",
        text:
            "I cried watching the fasolada video. My mother used to make it on Sundays before she passed. You brought her back to my kitchen for ten minutes.",
    },
    {
        name: "Andreas",
        location: "Limassol, Cyprus",
        text:
            "Thank you for telling the story behind kleftiko. My pappou was a Klepht — I never knew the dish carried that name for that reason. We watched together and he cried.",
    },
    {
        name: "Maria",
        location: "Toronto, Canada",
        text:
            "I'm teaching my daughter to cook from your videos. She doesn't speak Greek but she now knows what agrelia are, and why we go to the hills in March.",
    },
    {
        name: "Stavros",
        location: "Berlin, Germany",
        text:
            "Bought the ingredients on a Sunday morning, made the Rhodian meatballs, called my parents. Best phone call we've had in months. Σας ευχαριστώ.",
    },
    {
        name: "Sofia",
        location: "London, UK",
        text:
            "I've followed a hundred food channels. Yours is the only one that makes me feel something. Please don't ever stop.",
    },
    {
        name: "Nikos",
        location: "Athens, Greece",
        text:
            "I'm Greek and I learned things about my own food from your videos. Keep going — what you're doing matters.",
    },
];

const PHILOSOPHY = [
    { icon: Heart, label: "Family", note: "Recipes are how we keep them with us." },
    { icon: BookOpen, label: "History", note: "Every dish is a small piece of who we are." },
    { icon: Flame, label: "Tradition", note: "Made the way it was always made." },
    { icon: Users, label: "Hospitality", note: "Φιλοξενία — the open table is sacred." },
    { icon: Sparkles, label: "Storytelling", note: "Without the story, it is just a list of ingredients." },
    { icon: Globe, label: "The Diaspora", note: "Cooking for the Greeks the world scattered." },
];

const Initial = ({ name }) => (
    <div className="w-10 h-10 rounded-full bg-hl-secondary text-hl-bg flex items-center justify-center font-serif text-lg shrink-0">
        {name.charAt(0)}
    </div>
);

const OurStory = () => {
    return (
        <div data-testid="our-story-page">
            {/* HERO */}
            <section
                data-testid="our-story-hero"
                className="relative min-h-[88vh] flex items-end overflow-hidden"
            >
                <div className="absolute inset-0 overflow-hidden">
                    <div
                        className="absolute inset-0 bg-cover bg-center hl-slow-zoom"
                        style={{ backgroundImage: `url(${HERO_IMG})` }}
                    />
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-hl-brown/30 via-hl-brown/40 to-hl-brown/90" />
                <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-20 pt-40 w-full">
                    <p className="hl-fade-up font-script text-[#E8D9B5] text-3xl md:text-4xl mb-3">
                        haris & michael —
                    </p>
                    <h1 className="hl-fade-up hl-delay-1 font-serif text-hl-bg text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.98] tracking-tight max-w-5xl">
                        The Story of <span className="italic">iCookGreek</span>
                    </h1>
                    <p className="hl-fade-up hl-delay-2 mt-8 max-w-2xl text-hl-bg/85 text-xl md:text-2xl leading-relaxed font-serif italic">
                        Two Greeks — one Cypriot, one Greek — sharing the recipes,
                        stories, and soul of the cuisine that raised them.
                    </p>
                </div>
            </section>

            {/* INTRO */}
            <section
                data-testid="our-story-intro"
                className="max-w-4xl mx-auto px-6 lg:px-10 pt-24 md:pt-32 pb-16"
            >
                <div className="hl-divider mb-8 max-w-xs">
                    <span className="font-script text-2xl text-hl-secondary">how it began —</span>
                </div>
                <p className="hl-dropcap font-serif italic text-hl-text/90 text-xl md:text-2xl leading-[1.65]">
                    iCookGreek was created from a simple idea: food is more than
                    recipes. It is history, memory, family, and culture.
                </p>
                <p className="mt-6 text-hl-text/85 text-lg leading-relaxed">
                    We are two Greeks living abroad — one Cypriot, one Greek —
                    <span className="text-hl-secondary font-semibold"> Haris and Michael</span>.
                    When we met, we connected instantly through our love for our
                    culture, our traditions, and the food we grew up with. Very
                    quickly, that connection turned into a mission: to share the
                    stories and the soul behind Greek and Cypriot cuisine with
                    the world.
                </p>
                <p className="mt-5 text-hl-text/85 text-lg leading-relaxed">
                    What started as simple cooking videos slowly became something
                    much bigger. People were connecting not only with the recipes,
                    but with the history and emotions behind them. Some dishes
                    came from villages, others from times of war, poverty,
                    celebration, migration, and survival. Every recipe carried a
                    story worth preserving.
                </p>
                <p className="mt-5 text-hl-text/85 text-lg leading-relaxed italic">
                    Food is one of the strongest ways a culture survives through
                    time. That belief is the entire reason this cookbook exists.
                </p>
            </section>

            {/* TIMELINE */}
            <section
                data-testid="our-story-timeline"
                className="max-w-6xl mx-auto px-6 lg:px-10 py-24 md:py-32"
            >
                <div className="text-center mb-20">
                    <p className="font-script text-3xl text-hl-secondary">how we grew —</p>
                    <h2 className="font-serif text-4xl md:text-6xl text-hl-brown mt-2 leading-[1.05]">
                        From a kitchen <span className="italic">to a community</span>
                    </h2>
                </div>

                <div className="relative">
                    {/* vertical line */}
                    <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-hl-border md:-translate-x-1/2" aria-hidden="true" />

                    <div className="space-y-16 md:space-y-24">
                        {TIMELINE.map((t, i) => (
                            <div
                                key={t.no}
                                data-testid={`timeline-step-${t.no}`}
                                className={`relative grid md:grid-cols-2 gap-8 md:gap-16 items-center hl-fade-up`}
                            >
                                {/* dot */}
                                <div className="absolute left-6 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-hl-secondary ring-8 ring-hl-bg" />

                                <div className={`pl-16 md:pl-0 ${i % 2 === 0 ? "md:pr-12 md:text-right" : "md:order-2 md:pl-12"}`}>
                                    <p className="font-script text-2xl text-hl-secondary">
                                        {t.year}
                                    </p>
                                    <p className="font-serif italic text-hl-muted text-sm uppercase tracking-[0.3em] mt-1">
                                        Chapter {t.no}
                                    </p>
                                    <h3 className="font-serif text-3xl md:text-4xl text-hl-brown mt-3 leading-tight">
                                        {t.title}
                                    </h3>
                                </div>

                                <div className={`pl-16 md:pl-0 ${i % 2 === 0 ? "md:pl-12" : "md:order-1 md:pr-12"}`}>
                                    <p className="text-hl-text/85 text-lg leading-relaxed">
                                        {t.body}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section
                data-testid="our-story-stats"
                className="relative py-24 md:py-32 hl-grain"
                style={{
                    backgroundImage: `linear-gradient(rgba(58,42,31,0.93), rgba(58,42,31,0.95)), url(${KITCHEN_IMG})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundAttachment: "fixed",
                }}
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-10 text-hl-bg">
                    <div className="text-center mb-16">
                        <p className="font-script text-3xl text-[#E8D9B5]">by the numbers —</p>
                        <h2 className="font-serif text-4xl md:text-6xl mt-2 leading-tight">
                            A community we never imagined.
                        </h2>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                        {STATS.map((s, i) => (
                            <div
                                key={s.label}
                                data-testid={`stat-${i}`}
                                className={`hl-fade-up hl-delay-${Math.min(5, i + 1)} text-center md:text-left p-8 md:p-10 border border-hl-bg/15 rounded-sm bg-hl-bg/[0.04] backdrop-blur-sm`}
                            >
                                <s.icon className="w-5 h-5 text-[#E8D9B5] mx-auto md:mx-0" strokeWidth={1.4} />
                                <p className="font-serif text-5xl md:text-6xl mt-5 leading-none">
                                    {s.value}
                                </p>
                                <p className="text-[11px] uppercase tracking-[0.25em] text-hl-bg/70 mt-4">
                                    {s.label}
                                </p>
                            </div>
                        ))}
                    </div>

                    <p className="text-center text-hl-bg/55 text-xs uppercase tracking-[0.3em] mt-10">
                        Updated as the channel grows
                    </p>
                </div>
            </section>

            {/* PHILOSOPHY */}
            <section
                data-testid="our-story-philosophy"
                className="max-w-7xl mx-auto px-6 lg:px-10 py-28 md:py-36"
            >
                <div className="grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5 lg:sticky lg:top-24">
                        <p className="font-script text-3xl text-hl-secondary">our philosophy —</p>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-hl-brown mt-2 leading-[1.05]">
                            Greek food is more than <span className="italic">recipes.</span>
                        </h2>
                        <p className="mt-6 text-hl-muted text-lg leading-relaxed">
                            It's family. It's history. It's a village somewhere on a
                            hillside, an old radio in the corner, hands that knew
                            exactly how much salt without ever measuring. Every dish
                            we cook is a small act of remembering.
                        </p>
                    </div>

                    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
                        {PHILOSOPHY.map((p, i) => (
                            <div
                                key={p.label}
                                data-testid={`philosophy-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`hl-fade-up hl-delay-${Math.min(5, (i % 5) + 1)} p-7 bg-hl-surface border border-hl-border rounded-sm hl-shadow-warm`}
                            >
                                <p.icon className="w-5 h-5 text-hl-secondary" strokeWidth={1.4} />
                                <h3 className="font-serif text-2xl text-hl-brown mt-3">{p.label}</h3>
                                <p className="text-hl-muted leading-relaxed text-sm mt-2">
                                    {p.note}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* COMMUNITY / TESTIMONIALS */}
            <section
                data-testid="our-story-community"
                className="bg-hl-tinted/50 hl-grain py-28 md:py-36"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="text-center mb-16">
                        <p className="font-script text-3xl text-hl-secondary">words from the table —</p>
                        <h2 className="font-serif text-4xl md:text-6xl text-hl-brown mt-2 leading-tight">
                            What our <span className="italic">community</span> tells us
                        </h2>
                        <p className="mt-5 text-hl-muted max-w-xl mx-auto">
                            Messages and comments from Greeks, Cypriots, and food-lovers
                            around the world. The reason we keep cooking.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {TESTIMONIALS.map((t, i) => (
                            <figure
                                key={t.name + t.location}
                                data-testid={`testimonial-${i}`}
                                className={`relative hl-fade-up hl-delay-${Math.min(5, (i % 5) + 1)} p-8 bg-hl-surface rounded-sm border border-hl-border hl-shadow-warm flex flex-col justify-between`}
                            >
                                <Quote className="w-5 h-5 text-hl-secondary/60" strokeWidth={1.4} />
                                <blockquote className="font-serif italic text-hl-text/90 text-lg leading-relaxed mt-4 mb-6">
                                    "{t.text}"
                                </blockquote>
                                <figcaption className="flex items-center gap-3 pt-4 border-t border-hl-border/70">
                                    <Initial name={t.name} />
                                    <div>
                                        <p className="font-serif text-lg text-hl-brown leading-tight">{t.name}</p>
                                        <p className="text-[11px] uppercase tracking-[0.25em] text-hl-muted">{t.location}</p>
                                    </div>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                </div>
            </section>

            {/* CINEMATIC GALLERY */}
            <section
                data-testid="our-story-gallery"
                className="max-w-7xl mx-auto px-6 lg:px-10 py-28 md:py-36"
            >
                <div className="hl-divider mb-8 max-w-md mx-auto">
                    <span className="font-script text-3xl">behind the lens</span>
                </div>
                <h2 className="font-serif text-center text-4xl md:text-6xl text-hl-brown leading-tight max-w-3xl mx-auto">
                    The <span className="italic">small things</span> that became a brand
                </h2>

                <div className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
                    {GALLERY.map((g, i) => (
                        <figure
                            key={g.src}
                            data-testid={`gallery-${i}`}
                            className={`relative group overflow-hidden rounded-sm hl-shadow-warm hl-fade-up hl-delay-${Math.min(5, (i % 5) + 1)} ${i === 0 ? "md:col-span-2 md:row-span-2 aspect-square md:aspect-auto" : "aspect-square"}`}
                        >
                            <img
                                src={g.src}
                                alt={g.alt}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-hl-brown/85 via-hl-brown/15 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-500" />
                            <figcaption className="absolute bottom-4 left-5 right-5 font-script text-2xl md:text-3xl text-hl-bg">
                                {g.caption}
                            </figcaption>
                        </figure>
                    ))}
                </div>
            </section>

            {/* CLOSING QUOTE */}
            <section
                data-testid="our-story-quote"
                className="bg-hl-brown text-hl-bg py-32 md:py-44 hl-grain relative overflow-hidden"
            >
                <div className="absolute inset-0 opacity-15"
                     style={{ backgroundImage: `url(${KITCHEN_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                <div className="absolute inset-0 bg-gradient-to-b from-hl-brown via-hl-brown/95 to-hl-brown" />
                <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 text-center">
                    <Quote className="w-7 h-7 text-[#E8D9B5] mx-auto mb-8" strokeWidth={1.2} />
                    <p className="font-serif italic text-3xl md:text-5xl lg:text-6xl leading-[1.15]">
                        We don't just share recipes.
                        <br />
                        <span className="text-[#E8D9B5]">We share the stories behind them.</span>
                    </p>
                    <p className="font-script text-3xl text-[#E8D9B5] mt-12">
                        — iCookGreek
                    </p>
                </div>
            </section>

            {/* FOLLOW CTA */}
            <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24 md:py-32 text-center">
                <p className="font-script text-3xl text-hl-secondary">come cook with us —</p>
                <h2 className="font-serif text-4xl md:text-5xl text-hl-brown mt-2 leading-tight">
                    Find us on the platforms <span className="italic">we live on</span>
                </h2>
                <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
                    <a
                        href="https://tiktok.com/@icookgreek" target="_blank" rel="noreferrer"
                        data-testid="our-story-tiktok"
                        className="inline-flex items-center gap-2 px-6 py-3.5 border border-hl-brown text-hl-brown hover:bg-hl-brown hover:text-hl-bg rounded-full hl-btn-lift text-[11px] uppercase tracking-[0.3em]"
                    >
                        <Music2 className="w-4 h-4" strokeWidth={1.5} /> TikTok
                    </a>
                    <a
                        href="https://www.instagram.com/icookmike/?hl=en" target="_blank" rel="noreferrer"
                        data-testid="our-story-instagram"
                        className="inline-flex items-center gap-2 px-6 py-3.5 border border-hl-primary text-hl-primary hover:bg-hl-primary hover:text-hl-bg rounded-full hl-btn-lift text-[11px] uppercase tracking-[0.3em]"
                    >
                        <Instagram className="w-4 h-4" strokeWidth={1.5} /> Instagram
                    </a>
                    <a
                        href="https://www.youtube.com/@Icookmike" target="_blank" rel="noreferrer"
                        data-testid="our-story-youtube"
                        className="inline-flex items-center gap-2 px-6 py-3.5 border border-hl-secondary text-hl-secondary hover:bg-hl-secondary hover:text-hl-bg rounded-full hl-btn-lift text-[11px] uppercase tracking-[0.3em]"
                    >
                        <Youtube className="w-4 h-4" strokeWidth={1.5} /> YouTube
                    </a>
                    <a
                        href="mailto:greekicook@gmail.com"
                        data-testid="our-story-email"
                        className="inline-flex items-center gap-2 px-6 py-3.5 border border-hl-border text-hl-brown hover:bg-hl-tinted rounded-full hl-btn-lift text-[11px] uppercase tracking-[0.3em]"
                    >
                        <Mail className="w-4 h-4" strokeWidth={1.5} /> Email
                    </a>
                </div>
                <Link
                    to="/recipes"
                    className="inline-flex items-center gap-3 mt-12 text-[11px] uppercase tracking-[0.3em] text-hl-secondary hl-link"
                >
                    Or browse the recipe archive <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </Link>
            </section>
        </div>
    );
};

export default OurStory;
